import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  IconButton,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowBackIcon } from "@chakra-ui/icons";
import NavigationBar from "../NavigationBar";

const importAll = (r) =>
  r.keys().sort().map((key) => ({
    src: r(key),
    alt: key.replace("./", "").replace(/\.[^/.]+$/, ""),
  }));

const photos = importAll(
  require.context("../images/hongkong2025", false, /\.(png|jpe?g|webp|avif)$/i)
);

export default function HongKong2025() {
  const [activePhoto, setActivePhoto] = useState(null);

  const handleOpen = (photo) => {
    setActivePhoto(photo);
  };

  const handleClose = () => {
    setActivePhoto(null);
  };

  // Distribute photos across columns for masonry effect (left to right)
  const columnCount = { base: 1, sm: 2, lg: 3 };
  const distributePhotos = (cols) => {
    const columns = Array.from({ length: cols }, () => []);
    photos.forEach((photo, index) => {
      columns[index % cols].push(photo);
    });
    return columns;
  };

  return (
    <Box bg="gray.800" minH="100vh" color="white">
      <NavigationBar />
      <Box h={{ base: 20, md: 24 }} />
      <Container maxW="7xl" pb={{ base: 12, md: 16 }}>
        <Stack spacing={8}>
          <Stack spacing={3} align="flex-start">
            <IconButton
              as={RouterLink}
              to="/photos"
              aria-label="Back to photo index"
              icon={<ArrowBackIcon />}
              variant="ghost"
              colorScheme="white"
              size="lg"
            />
            <Heading size="xl" color="white">Hong Kong, Macau, Shenzhen</Heading>
          </Stack>

          {/* Mobile: 1 column */}
          <Box display={{ base: "block", sm: "none" }}>
            <Box>
              {distributePhotos(1)[0].map((photo) => (
                <Box
                  key={photo.src}
                  mb={4}
                  borderRadius="lg"
                  overflow="hidden"
                  borderWidth="1px"
                  borderColor="gray.700"
                  bg="gray.900"
                  _hover={{ borderColor: "gray.500", cursor: "pointer" }}
                  onClick={() => handleOpen(photo)}
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    loading="lazy"
                    width="100%"
                    height="auto"
                    objectFit="cover"
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Tablet: 2 columns */}
          <Box display={{ base: "none", sm: "flex", lg: "none" }} gap={4}>
            {distributePhotos(2).map((column, colIndex) => (
              <Box key={colIndex} flex="1">
                {column.map((photo) => (
                  <Box
                    key={photo.src}
                    mb={4}
                    borderRadius="lg"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.700"
                    bg="gray.900"
                    _hover={{ borderColor: "gray.500", cursor: "pointer" }}
                    onClick={() => handleOpen(photo)}
                  >
                    <Image
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      width="100%"
                      height="auto"
                      objectFit="cover"
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {/* Desktop: 3 columns */}
          <Box display={{ base: "none", lg: "flex" }} gap={4}>
            {distributePhotos(3).map((column, colIndex) => (
              <Box key={colIndex} flex="1">
                {column.map((photo) => (
                  <Box
                    key={photo.src}
                    mb={4}
                    borderRadius="lg"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.700"
                    bg="gray.900"
                    _hover={{ borderColor: "gray.500", cursor: "pointer" }}
                    onClick={() => handleOpen(photo)}
                  >
                    <Image
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      width="100%"
                      height="auto"
                      objectFit="cover"
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Stack>
      </Container>

      {activePhoto && (
        <Modal 
          isOpen={true} 
          onClose={handleClose} 
          size="full" 
          isCentered
          motionPreset="none"
        >
          <ModalOverlay bg="blackAlpha.900" onClick={handleClose} />
          <ModalContent bg="transparent" boxShadow="none" pointerEvents="none">
            <ModalCloseButton color="white" zIndex={2} pointerEvents="auto" />
            <ModalBody display="flex" alignItems="center" justifyContent="center" p={0} h="100vh" pointerEvents="auto">
              <Image
                src={activePhoto.src}
                alt={activePhoto.alt}
                maxW="95vw"
                maxH="95vh"
                objectFit="contain"
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}
